import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface RedirectEntry {
  target: string;
  permanent: boolean;
  external: boolean;
}

export interface RedirectResult {
  hasRedirect: boolean;
  targetUrl?: string;
  isExternal: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private http = inject(HttpClient);
  private redirects = new Map<string, RedirectEntry>();
  private loaded = false;

  loadRedirects(): Promise<void> {
    if (this.loaded) return Promise.resolve();

    // Load from static JSON asset (generated at build time from rewritemap.config)
    return firstValueFrom(this.http.get<Record<string, RedirectEntry>>('/assets/redirects.json'))
      .then((data) => {
        this.redirects = new Map(Object.entries(data));
        this.loaded = true;
      })
      .catch((err) => {
        console.error('[RedirectService] Failed to load redirects.json:', err);
        this.loaded = true; // Mark as loaded to prevent retry loops
      });
  }

  checkRedirect(path: string): RedirectResult {
    if (!this.loaded) {
      return { hasRedirect: false, isExternal: false };
    }

    const normalizedPath = this.normalizePath(path);
    let entry = this.redirects.get(normalizedPath);

    if (!entry) {
      const pathWithoutTrailing = normalizedPath.replace(/\/$/, '');
      if (pathWithoutTrailing !== normalizedPath) {
        entry = this.redirects.get(pathWithoutTrailing);
      }
    }

    if (entry) {
      return {
        hasRedirect: true,
        targetUrl: entry.target,
        isExternal: entry.external
      };
    }

    return { hasRedirect: false, isExternal: false };
  }

  private normalizePath(path: string): string {
    if (!path) return '/';
    let cleanPath = path.split('?')[0].split('#')[0];
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    return cleanPath.toLowerCase();
  }
}
